import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

import api from '../api';

const COLORS = {
  total_weight: '#1a1a1a',
  total_reps: '#007aff',
  avg_per_rep: '#34c759',
  body_mass: '#ff9500',
};

const styles = {
  container: {
    maxWidth: 480,
    margin: '0 auto',
    background: '#fff',
    minHeight: '100vh',
    paddingBottom: 60,
  },
  header: {
    padding: '1.25rem',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 10,
  },
  backBtn: {
    border: 'none',
    background: 'none',
    fontSize: 15,
    color: '#888',
    padding: 0,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  filterRow: {
    display: 'flex',
    gap: 8,
    padding: '0.75rem 1.25rem',
    borderBottom: '1px solid #f0f0f0',
    overflowX: 'auto',
  },
  filterBtn: {
    padding: '6px 14px',
    borderRadius: 20,
    border: '1px solid #ebebeb',
    background: '#fff',
    fontSize: 13,
    color: '#888',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  filterBtnActive: {
    padding: '6px 14px',
    borderRadius: 20,
    border: '1px solid #1a1a1a',
    background: '#1a1a1a',
    fontSize: 13,
    color: '#fff',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  chartSection: {
    padding: '1.25rem 1.25rem 0',
  },
  chartTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#888',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: '0.75rem',
  },
  chartWrap: {
    width: '100%',
    height: 200,
    marginBottom: '1.5rem',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem 1.25rem',
    color: '#aaa',
    fontSize: 14,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    padding: '0 1.25rem 1.25rem',
  },
  summaryCard: {
    background: '#f8f8f8',
    borderRadius: 12,
    padding: '1rem',
  },
  summaryLabel: {
    fontSize: 11,
    color: '#888',
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1a1a1a',
  },
  summarySub: {
    fontSize: 11,
    color: '#aaa',
    marginTop: 2,
  },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: '#fff',
        border: '1px solid #ebebeb',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 12,
      }}>
        <div style={{ color: '#888', marginBottom: 4 }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color, fontWeight: 600 }}>
            {p.value?.toLocaleString()} {p.name === 'total_weight' ? 'lbs' : p.name === 'total_reps' ? 'reps' : p.name === 'avg_per_rep' ? 'lbs/rep' : 'lbs'}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function ProgressScreen({ onBack }) {
  const [sessions, setSessions] = useState([]);
  const [workoutTypes, setWorkoutTypes] = useState([]);
  const [activeType, setActiveType] = useState('All');

  useEffect(() => {
    api.get('/sessions').then(r => setSessions(r.data));
    api.get('/workout-types').then(r => setWorkoutTypes(r.data));
  }, []);

  const filtered = sessions
    .filter(s => activeType === 'All' || s.workout_type_name === activeType)
    .slice()
    .reverse()
    .map(s => ({
      ...s,
      label: new Date(s.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }));

  const best = (key) => {
    if (!filtered.length) return '—';
    return Math.max(...filtered.map(s => s[key] || 0)).toLocaleString();
  };

  const avg = (key) => {
    if (!filtered.length) return '—';
    const vals = filtered.filter(s => s[key] > 0).map(s => s[key]);
    if (!vals.length) return '—';
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  };

  const hasBodyMass = filtered.some(s => s.body_mass);

  if (sessions.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={onBack}>← Back</button>
          <span style={styles.headerTitle}>Progress</span>
          <span />
        </div>
        <div style={styles.empty}>Log some workouts first to see your progress!</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <span style={styles.headerTitle}>Progress</span>
        <span style={{ fontSize: 12, color: '#aaa' }}>{filtered.length} sessions</span>
      </div>

      <div style={styles.filterRow}>
        {['All', ...workoutTypes.map(t => t.name)].map(type => (
          <button
            key={type}
            style={activeType === type ? styles.filterBtnActive : styles.filterBtn}
            onClick={() => setActiveType(type)}
          >
            {type}
          </button>
        ))}
      </div>

      {filtered.length < 2 ? (
        <div style={styles.empty}>Log at least 2 sessions to see charts.</div>
      ) : (
        <>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Best session</div>
              <div style={styles.summaryValue}>{best('total_weight')}</div>
              <div style={styles.summarySub}>lbs total weight</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Avg weight / session</div>
              <div style={styles.summaryValue}>{avg('total_weight')}</div>
              <div style={styles.summarySub}>lbs per session</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Best reps</div>
              <div style={styles.summaryValue}>{best('total_reps')}</div>
              <div style={styles.summarySub}>reps in a session</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Avg reps / session</div>
              <div style={styles.summaryValue}>{avg('total_reps')}</div>
              <div style={styles.summarySub}>reps per session</div>
            </div>
          </div>

          <div style={styles.chartSection}>
            <div style={styles.chartTitle}>Total weight moved</div>
            <div style={styles.chartWrap}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={filtered}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={55} tickFormatter={v => v.toLocaleString()} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="total_weight"
                    stroke={COLORS.total_weight}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={styles.chartTitle}>Total reps</div>
            <div style={styles.chartWrap}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={filtered}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={40} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="total_reps"
                    stroke={COLORS.total_reps}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={styles.chartTitle}>Avg weight per rep</div>
            <div style={styles.chartWrap}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={filtered}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={40} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="avg_per_rep"
                    stroke={COLORS.avg_per_rep}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {hasBodyMass && (
              <>
                <div style={styles.chartTitle}>Body mass</div>
                <div style={styles.chartWrap}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={filtered.filter(s => s.body_mass)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={40} domain={['auto', 'auto']} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="body_mass"
                        stroke={COLORS.body_mass}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}